import React, { memo, useState, useRef, useEffect } from 'react';
import { useField } from "formik";
import classnames from "classnames";
import s from "./SocialSelect.module.css";
import facebookIcon from '../../../assets/icons/FacebookIcon.svg';
import signalIcon from '../../../assets/icons/SignalIcon.svg';
import tgIcon from '../../../assets/icons/tgIcon.svg';
import weChatIcon from '../../../assets/icons/WeChatIcon.svg';
import whatsAppIcon from '../../../assets/icons/whatsappIcon.svg';

type SocialType = {
    id: string;
    name: string;
    icon: string;
    placeholder: string;
}

type propsType = {
    name?: string;
    value?: string;
    error?: any;
    touched?: any;
    onChange?: any;
    label?: string;
    disabled?: boolean;
}

const socials: SocialType[] = [
    { id: 'telegram', name: 'Telegram', placeholder: '@username or number', icon: tgIcon },
    { id: 'whatsapp', name: 'WhatsApp', placeholder: '+4 999 999-99-99', icon: whatsAppIcon },
    { id: 'WeChat', name: 'WeChat', placeholder: '+4 999 999-99-99', icon: weChatIcon },
    { id: 'Signal', name: 'Signal', placeholder: '@username', icon: signalIcon },
    { id: 'facebook', name: 'Messenger', placeholder: 'username', icon: facebookIcon },
];

const SocialSelect = memo((props: propsType) => {
    const [field, meta, helpers] = useField(props.name || 'clientSocialMedia');
    const { setValue } = helpers;

    const [isOpen, setIsOpen] = useState(false);
    const [selectedSocial, setSelectedSocial] = useState<SocialType>(socials[0]);
    const [inputValue, setInputValue] = useState('');
    const dropdownRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);

    const error = props.error || meta.error;
    const touched = props.touched || meta.touched;

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Формируем строку в формате "Соцсеть: контакт"
    const formatSocialString = (social: SocialType, contact: string) => {
        if (!contact || contact.trim() === '') {
            return ''; // Если контакт пустой, возвращаем пустую строку
        }
        return `${social.name}: ${contact.trim()}`;
    };

    const handleSelectSocial = (social: SocialType) => {
        setSelectedSocial(social);
        setIsOpen(false);
        setTimeout(() => inputRef.current?.focus(), 0);

        // Обновляем Formik с новым форматом
        const formattedValue = formatSocialString(social, inputValue);
        setValue(formattedValue);
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const newValue = e.target.value;
        setInputValue(newValue);

        // Обновляем Formik с текущей соцсетью и новым контактом
        const formattedValue = formatSocialString(selectedSocial, newValue);
        setValue(formattedValue);

        if (props.onChange) {
            props.onChange(formattedValue);
        }
    };

    // Инициализация из props.value (парсим строку "Соцсеть: контакт")
    useEffect(() => {
        const rawValue = props.value || field.value;
        if (rawValue && typeof rawValue === 'string' && rawValue.includes(':')) {
            const [socialName, ...contactParts] = rawValue.split(':');
            const contact = contactParts.join(':').trim();

            const foundSocial = socials.find(s => s.name === socialName.trim());
            if (foundSocial) {
                setSelectedSocial(foundSocial);
                setInputValue(contact);
            } else {
                setInputValue(rawValue);
            }
        } else if (rawValue && typeof rawValue === 'string') {
            setInputValue(rawValue);
        }
    }, [props.value, field.value]);

    const showError = error && touched;

    return (
        <div className={s.container}>
            {props.label && (
                <label className={s.label}>{props.label}</label>
            )}

            <div className={s.input_wrapper}>
                <div className={s.dropdown_wrapper} ref={dropdownRef}>
                    <button
                        type="button"
                        className={classnames(s.dropdown_trigger, {
                            [s.error]: showError,
                            [s.disabled]: props.disabled
                        })}
                        onClick={() => !props.disabled && setIsOpen(!isOpen)}
                        disabled={props.disabled}
                    >
                        <img src={selectedSocial.icon} alt={selectedSocial.name} className={s.icon} />
                        <span className={classnames(s.arrow, { [s.open]: isOpen })}>▼</span>
                    </button>

                    {isOpen && (
                        <div className={s.dropdown_menu}>
                            {socials.map(social => (
                                <button
                                    key={social.id}
                                    type="button"
                                    className={classnames(s.dropdown_item, {
                                        [s.selected]: selectedSocial.id === social.id
                                    })}
                                    onClick={() => handleSelectSocial(social)}
                                >
                                    <img src={social.icon} alt={social.name} className={s.icon} />
                                    <span className={s.name}>{social.name}</span>
                                </button>
                            ))}
                        </div>
                    )}
                </div>

                <input
                    ref={inputRef}
                    type="text"
                    className={classnames(s.input_field, {
                        [s.error]: showError,
                        [s.disabled]: props.disabled
                    })}
                    value={inputValue}
                    onChange={handleInputChange}
                    placeholder={selectedSocial.placeholder}
                    disabled={props.disabled}
                />
            </div>

            {showError && (
                <div className={s.error_message}>{error}</div>
            )}
        </div>
    );
});

export default SocialSelect;